import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  Paper,
  Alert,
} from '@mui/material';
import {
  Mail,
  Phone,
  Globe,
  Send,
  CheckCircle2,
  ShieldCheck,
} from 'lucide-react';

import LogoImg from '../assets/AcoSignFavIcon.png';

const DEFAULT_CLIENT_ID = '1f03088f-28cb-6ac0-86a7-8a56d9a1daba';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    interest: 'Acolead CRM',
    requirement: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const interestOptions = [
    'Acolead CRM',
    'Instagram & Facebook AI',
    'WhatsApp Business API',
    'AI Calling Solutions',
    'Other Business Enquiry',
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const clientId = process.env.REACT_APP_ACOLEAD_CLIENT_ID || DEFAULT_CLIENT_ID;
      const requirementText = formData.requirement
        ? `[Interest: ${formData.interest}] ${formData.requirement}`
        : `[Interest: ${formData.interest}]`;

      let result;
      if (window.AcoLead && typeof window.AcoLead.submitEnquiry === 'function') {
        result = await window.AcoLead.submitEnquiry(
          clientId,
          formData.fullName,
          formData.phone,
          requirementText,
          formData.email,
          formData.companyName || ''
        );
      } else {
        const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        const endpoint = isLocal
          ? `/crm-backend/api/v1/cp/${clientId}/capture-lead`
          : `https://api.acolead.com/crm-backend/api/v1/cp/${clientId}/capture-lead`;

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.fullName,
            mobileNumber: formData.phone,
            requirement: requirementText,
            email: formData.email,
            company: formData.companyName || '',
            type: 'ENQUIRY',
          }),
        });

        if (!response.ok) {
          throw new Error(`Submission failed with status: ${response.status}`);
        }
        result = await response.json();
      }

      if (result && (result.code || result.isAxiosError || result instanceof Error)) {
        console.error('CRM lead submission error:', result);
        setErrorMessage('We could not submit your request. Please try again shortly.');
        return;
      }

      setSubmitted(true);
      setFormData({
        fullName: '',
        companyName: '',
        email: '',
        phone: '',
        interest: 'Acolead CRM',
        requirement: '',
      });
    } catch (error) {
      console.error('Error during lead submission:', error);
      setErrorMessage('We could not submit your request. Please try again shortly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* ========================================================================= */}
      {/* 1. CONTACT HERO */}
      {/* ========================================================================= */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: 'radial-gradient(ellipse at 50% 0%, rgba(37, 99, 235, 0.08) 0%, rgba(255, 255, 255, 0) 75%)',
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center' }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.8rem', sm: '3.4rem', md: '4.2rem' },
                lineHeight: 1.12,
                letterSpacing: '-0.035em',
                color: '#0F172A',
                mb: 2,
              }}
            >
              Let's Start a <span className="text-gradient-emerald">Conversation</span>
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: '#475569',
                fontSize: { xs: '1.1rem', md: '1.35rem' },
              }}
            >
              Tell Us What You're Looking For
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 2. FORM & CONTACT INFO SECTION */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#F8FAFC' }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 8 }}>
            {/* Left Column: Interactive Contact Form */}
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, md: 5 },
                  width: '100%',
                  maxWidth: { xs: '100%', md: 820 },
                  ml: { xs: 0, md: 2 },
                  borderRadius: 4,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  boxShadow: '0 20px 40px -10px rgba(15, 23, 42, 0.06)',
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', mb: 1, fontSize: '1.65rem' }}>
                  Send Us an Enquiry
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748B', mb: 4 }}>
                  Fill out the form below and our team will get back to you promptly.
                </Typography>

                {submitted && (
                  <Alert
                    icon={<CheckCircle2 size={22} />}
                    severity="success"
                    onClose={() => setSubmitted(false)}
                    sx={{ mb: 4, borderRadius: 2.5 }}
                  >
                    Thank you for your enquiry! Our team has received your message and will reach out shortly.
                  </Alert>
                )}

                {errorMessage && (
                  <Alert
                    severity="error"
                    onClose={() => setErrorMessage('')}
                    sx={{ mb: 4, borderRadius: 2.5 }}
                  >
                    {errorMessage}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.8 }}>
                        Full Name*
                      </Typography>
                      <TextField
                        required
                        fullWidth
                        name="fullName"
                        placeholder="e.g. Rahul Sharma"
                        value={formData.fullName}
                        onChange={handleChange}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: { borderRadius: 10 },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.8 }}>
                        Company Name
                      </Typography>
                      <TextField
                        fullWidth
                        name="companyName"
                        placeholder="e.g. Apex Realty Developers"
                        value={formData.companyName}
                        onChange={handleChange}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: { borderRadius: 10 },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.8 }}>
                        Business Email*
                      </Typography>
                      <TextField
                        required
                        type="email"
                        fullWidth
                        name="email"
                        placeholder="e.g. name@company.com"
                        value={formData.email}
                        onChange={handleChange}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: { borderRadius: 10 },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.8 }}>
                        Phone Number*
                      </Typography>
                      <TextField
                        required
                        fullWidth
                        name="phone"
                        placeholder="e.g. +91 98765 43210"
                        value={formData.phone}
                        onChange={handleChange}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: { borderRadius: 10 },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.8 }}>
                        What are you interested in?*
                      </Typography>
                      <TextField
                        select
                        required
                        fullWidth
                        name="interest"
                        value={formData.interest}
                        onChange={handleChange}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: { borderRadius: 10 },
                        }}
                      >
                        {interestOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 0.8 }}>
                        Tell Us About Your Requirement
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        name="requirement"
                        placeholder="Share details about your team size, software needs, or business goals..."
                        value={formData.requirement}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                          style: { borderRadius: 10 },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={loading}
                        endIcon={<Send size={18} />}
                        sx={{
                          py: 1.6,
                          px: 4.5,
                          borderRadius: '12px',
                          fontWeight: 700,
                          fontSize: '1.02rem',
                          width: { xs: '100%', sm: 'auto' },
                          background: 'linear-gradient(135deg, #2563EB 0%, #059669 100%)',
                          boxShadow: '0 12px 28px -6px rgba(16, 185, 129, 0.4)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1D4ED8 0%, #047857 100%)',
                            boxShadow: '0 16px 36px -4px rgba(16, 185, 129, 0.55)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        {loading ? 'Submitting...' : 'Submit Enquiry'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            {/* Right Column: Contact Information */}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 3.5 },
                    borderRadius: 4,
                    backgroundColor: '#070D19',
                    color: '#FFFFFF',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 3 }}>
                    <Box
                      component="img"
                      src={LogoImg}
                      alt="Coalitionify Logo"
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '10px',
                        objectFit: 'contain',
                        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.4)',
                      }}
                    />
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#FFFFFF', lineHeight: 1.1 }}>
                        Coalitionify Innovate
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, display: 'block' }}>
                        Private Limited
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          backgroundColor: 'rgba(37, 99, 235, 0.15)',
                          color: '#60A5FA',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Mail size={20} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, display: 'block' }}>
                          OFFICIAL EMAIL
                        </Typography>
                        <Typography
                          component="a"
                          href="mailto:info@coalitionify.com"
                          variant="body1"
                          sx={{ color: '#FFFFFF', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#60A5FA' } }}
                        >
                          info@coalitionify.com
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          backgroundColor: 'rgba(16, 185, 129, 0.15)',
                          color: '#34D399',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Phone size={20} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, display: 'block' }}>
                          OFFICIAL PHONE
                        </Typography>
                        <Typography
                          component="a"
                          href="tel:+919112614174"
                          variant="body1"
                          sx={{ color: '#FFFFFF', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#34D399' } }}
                        >
                          +91 91126 14174
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          backgroundColor: 'rgba(79, 70, 229, 0.15)',
                          color: '#A78BFA',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Globe size={20} />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, display: 'block' }}>
                          OFFICIAL WEBSITE
                        </Typography>
                        <Typography
                          component="a"
                          href="https://www.coalitionify.com"
                          variant="body1"
                          sx={{ color: '#FFFFFF', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#A78BFA' } }}
                        >
                          www.coalitionify.com
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <ShieldCheck size={22} style={{ color: '#2563EB' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A' }}>
                      Enterprise Assurance
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.65 }}>
                    All enquiries are kept strictly confidential under our privacy standards. Our enterprise team typical responds within 24 business hours.
                  </Typography>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default ContactPage;
